// GA4 引导必须最先求值（dataLayer 桩在任何 React 埋点前就绪；见 lib/ga-boot.ts）；
// 广场预取紧随其后（消费 prerender 注入的 preload，见 lib/plaza-prefetch.ts）
import "./lib/ga-boot";
import "./lib/plaza-prefetch";
import { StrictMode, useState, useEffect } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { ClerkProvider } from "@clerk/clerk-react";
import { zhCN, jaJP, koKR, enUS } from "@clerk/localizations";
import i18n from "./i18n";
import { detectTheme, applyTheme } from "./lib/theme";
import { detectLang } from "./lib/lang";
import App from "./App";
import ErrorBoundary from "./components/ErrorBoundary";
import "./index.css";

// 模块加载即应用主题（双保险：index.html 已静态写 data-theme，此处补齐）
// Tsuvi 固定深色：applyTheme 仅写 <html data-theme="dark">。
applyTheme(detectTheme());

const PUBLISHABLE_KEY = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;

if (!PUBLISHABLE_KEY) {
  throw new Error("Missing VITE_CLERK_PUBLISHABLE_KEY");
}

/**
 * 全局兜底降级 UI：任何未被内层 boundary 接住的渲染错误都到此，
 * 保证不会整页白屏。提供"返回首页"与"刷新"两条出路。
 * i18next 已在模块加载时初始化，此处可用 t()，但错误边界可能早于语言同步触发，
 * 故用 i18n.t 直接取当前语言，避免 useTranslation 在无 Provider 时报错。
 */
function RootErrorFallback() {
  return (
    <div
      className="error-screen"
      role="alert"
      style={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: "0.75rem",
        padding: "2rem",
        textAlign: "center",
        fontFamily: "system-ui, sans-serif",
      }}
    >
      <h1 style={{ margin: 0 }}>{i18n.t("errors.rootTitle")}</h1>
      <p style={{ margin: 0, color: "#b89e92" }}>{i18n.t("errors.rootBody")}</p>
      <div style={{ display: "flex", gap: "0.5rem", marginTop: "0.5rem" }}>
        <a className="link-btn" href="/" style={{ textDecoration: "none" }}>
          {i18n.t("common.backHome")}
        </a>
        <button
          type="button"
          className="link-btn"
          onClick={() => window.location.reload()}
        >
          {i18n.t("common.reload")}
        </button>
      </div>
    </div>
  );
}

/** i18n 语言 → Clerk 本地化包映射（未命中走 enUS 兜底） */
const CLERK_LOCALIZATIONS: Record<string, typeof enUS> = {
  zh: zhCN,
  ja: jaJP,
  ko: koKR,
  en: enUS,
};

/**
 * Clerk 初始语言：URL 的 /:lang 前缀优先（URL 是语言唯一真相，见 LangSync），
 * 无前缀时回退 detectLang()（与根路径重定向逻辑一致）。
 * 不能用 i18n.language：i18n 启动固定 en，用它会让 Clerk 实例以英文构造，
 * 挂载后靠 updateProps 补偿存在加载竞态（实测冷加载 /zh 时 Clerk 菜单停留英文，
 * 而 SPA 运行时切换正常——故首屏必须构造时就带对 localization）。
 */
function initialClerkLang(): string {
  const m = window.location.pathname.match(/^\/(en|zh|ja|ko)(?:\/|$)/);
  return m ? m[1] : detectLang();
}

/**
 * ClerkProvider 包裹：让 Clerk UI（登录/注册/UserButton）跟随当前 i18n 语言。
 * ClerkProvider 在 Router 外，无法用 useLang()；初始值取 URL 前缀，
 * 运行时切换靠监听 i18n languageChanged（LangSync 在 /:lang 变化时触发）。
 */
function ClerkProviderWithI18n({ children }: { children: React.ReactNode }) {
  const [lang, setLang] = useState(initialClerkLang);
  useEffect(() => {
    const handler = (newLang: string) => setLang(newLang);
    i18n.on("languageChanged", handler);
    return () => {
      i18n.off("languageChanged", handler);
    };
  }, []);
  return (
    <ClerkProvider
      publishableKey={PUBLISHABLE_KEY}
      localization={CLERK_LOCALIZATIONS[lang] ?? enUS}
    >
      {children}
    </ClerkProvider>
  );
}

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <ClerkProviderWithI18n>
      <ErrorBoundary fallback={<RootErrorFallback />}>
        <BrowserRouter>
          <App />
        </BrowserRouter>
      </ErrorBoundary>
    </ClerkProviderWithI18n>
  </StrictMode>,
);
